/**
 * Dashboard Gerencial — VEN-08: KPIs ejecutivos + gráficos de tendencia (recharts)
 */
import { todayISO } from '../../lib/utils'
import { tenantLocale, tenantCurrency } from '@/lib/locale'
import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import DatePicker from '@/components/DatePicker'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import {
  FileText, Users, DollarSign, AlertTriangle, TrendingUp,
  TrendingDown, Scale, ShoppingBag, Building2, Download, Loader2
} from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { apiDownload, getApiErrorMessage } from '@/lib/apiClient'
import { useToast } from '@/components/Toast'
import { useAuthStore } from '@/store/authStore'
import { ROLE_NAMES, type RoleType } from '@/types'
import { useDashboardMetrics, type DashboardMetrics } from '@/modules/sales/hooks/useSales'
import { useCurrencyRates } from '@/modules/config/hooks/useConfig'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import DashboardBackdrop from './DashboardBackdrop'
import { StatusBadge, DTE_STATUS } from '@/components/ds/StatusBadge'

/** `'2026-08-21'` → `'21-08-2026'`, que es cómo se lee una fecha de corte en Chile. */
function fmtFecha(iso?: string): string {
  if (!iso || iso.length !== 10) return 'hoy'
  const [a, m, d] = iso.split('-')
  return `${d}-${m}-${a}`
}

/** La descarga del saldo a la misma fecha de corte que muestra la tarjeta. */
function rutaSaldo(path: string, corte?: string): string {
  return corte ? `${path}?as_of_date=${corte}` : path
}

const MONTH_NAMES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
]

/*
 * Los dos mapas de estado que vivían acá se fueron a `DTE_STATUS`.
 *
 * 🔴 Este era el más divergente de los tres: pintaba `xml_generated` de cyan
 * donde el listado lo pone violeta, `sent_facturador` de púrpura donde es
 * primario, `querying_status` de naranja donde es cyan, y `accepted`/`rejected`
 * con paletas crudas de Tailwind. Y **`pending_send` no estaba**, así que un
 * documento «Por Enviar» caía al gris del fallback, indistinguible de un
 * borrador — justo en la pantalla que se mira para saber qué falta emitir.
 */

function fmt(value: string | number | undefined, currency = true, currencyCode = '') {
  if (value === undefined || value === null) return '—'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '—'
  if (currency) {
    // Sin `maximumFractionDigits`: Intl ya sabe cuántos decimales lleva cada
    // moneda, y forzar 0 le come los céntimos a un total en soles.
    return new Intl.NumberFormat(tenantLocale(), {
      style: 'currency', currency: currencyCode || tenantCurrency(),
    }).format(num)
  }
  return num.toLocaleString(tenantLocale())
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

// KPI card color map — cada estado financiero tiene un color distintivo. Los
// valores dark: usan la variante 900/30 del color (pastel oscuro translúcido)
// + texto 200 (pastel claro) para mantener el código de color legible en dark.
// En light: card bg-50 + icon container bg-100 (un tono más saturado) + icon text-600
// En dark:  card bg-900/15 + icon container bg-500/40 (un tono más claro que el card para contraste) + icon text-100 (muy claro)
const colorMap = {
  blue:   { bg: 'bg-primary-50/80 dark:bg-primary-900/30',  icon: 'bg-primary-100 text-primary dark:bg-primary-500/40 dark:text-white',       text: 'text-primary-hover dark:text-primary-200' },
  green:  { bg: 'bg-green-50/80 dark:bg-green-900/30',      icon: 'bg-green-100 text-green-600 dark:bg-green-500/40 dark:text-green-50',     text: 'text-green-700 dark:text-green-200' },
  amber:  { bg: 'bg-warning-surface',      icon: 'bg-warning-surface-strong text-warning-content-soft',     text: 'text-warning-content' },
  purple: { bg: 'bg-purple-50/80 dark:bg-purple-900/30',    icon: 'bg-purple-100 text-purple-600 dark:bg-purple-500/40 dark:text-purple-50', text: 'text-purple-700 dark:text-purple-200' },
  orange: { bg: 'bg-orange-50/80 dark:bg-orange-900/30',    icon: 'bg-orange-100 text-orange-600 dark:bg-orange-500/40 dark:text-orange-50', text: 'text-orange-700 dark:text-orange-200' },
  teal:   { bg: 'bg-teal-50/80 dark:bg-teal-900/30',        icon: 'bg-teal-100 text-teal-600 dark:bg-teal-500/40 dark:text-teal-50',         text: 'text-teal-700 dark:text-teal-200' },
} as const

export function KPICard({
  title, value, subtitle, icon: Icon, color, tooltip, linkTo, download,
}: {
  title: string; value: string | number; subtitle: string
  icon: React.ElementType; color: keyof typeof colorMap
  tooltip?: string
  linkTo?: string
  /**
   * Descarga el detalle que suma este número. Combinable con `linkTo`: cuando
   * están los dos, el cuadrito del ícono descarga y el resto de la tarjeta
   * navega. Con `download` solo, toda la tarjeta descarga.
   */
  download?: { path: string; filename: string; hint?: string }
}) {
  const c = colorMap[color]
  const [show, setShow] = useState(false)
  const [bajando, setBajando] = useState(false)
  const toast = useToast()
  const timeout = useRef<ReturnType<typeof setTimeout>>()

  const descargar = async () => {
    if (!download || bajando) return
    setBajando(true)
    try {
      await apiDownload(download.path, download.filename)
    } catch (e) {
      toast.error(getApiErrorMessage(e))
    } finally {
      setBajando(false)
    }
  }

  const onEnter = () => { clearTimeout(timeout.current); timeout.current = setTimeout(() => setShow(true), 300) }
  const onLeave = () => { clearTimeout(timeout.current); setShow(false) }

  const baseCls = `${c.bg} backdrop-blur-sm rounded-xl p-5 border border-surface dark:border-divider shadow-sm relative ${show ? 'z-10' : 'z-0'}`
  const linkCls = (linkTo || download)
    ? ' cursor-pointer transition hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary/40'
    : ''

  // Con descarga, el ícono se convierte en la señal de que ahí se baja un
  // archivo: una tarjeta que descarga sin avisar es sorpresiva.
  const glifo = download && (show || bajando)
    ? (bajando ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />)
    : <Icon size={18} />
  const cuadroCls = `${c.icon} w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ml-2`

  const cuerpo = (
    <div className="min-w-0">
      <p className="text-xs font-medium text-content-muted uppercase tracking-wide">{title}</p>
      <p className="text-lg sm:text-xl font-bold text-content mt-1 tabular-nums break-words">{value}</p>
      <p className="text-xs text-content-subtle mt-0.5">{subtitle}</p>
    </div>
  )

  const globo = tooltip && show && (
    <div role="tooltip" className="absolute z-50 left-0 right-0 top-full mt-1 mx-2 bg-popover text-content border border-divider-strong text-xs rounded-lg px-3 py-2 shadow-lg leading-relaxed pointer-events-none">
      {tooltip}
    </div>
  )

  const inner = (
    <>
      <div className="flex items-start justify-between">
        {cuerpo}
        <div className={cuadroCls}>{glifo}</div>
      </div>
      {globo}
    </>
  )

  // Las dos acciones a la vez. El link NO envuelve la tarjeta: va absoluto
  // cubriéndola, y el botón queda por encima. Envolver dejaría un <button>
  // dentro de un <a> —HTML inválido— y el clic en el ícono lo capturaría el
  // link, que es justo lo que hay que evitar.
  if (linkTo && download) {
    return (
      <div className={`${baseCls}${linkCls}`} onMouseEnter={onEnter} onMouseLeave={onLeave}>
        <Link
          to={linkTo}
          className="absolute inset-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          aria-label={`${title}: ver el detalle`}
        />
        <div className="flex items-start justify-between pointer-events-none">
          {cuerpo}
          <button
            type="button"
            onClick={descargar}
            disabled={bajando}
            className={`${cuadroCls} pointer-events-auto relative z-10 transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-70`}
            aria-label={`${title}: descargar el detalle en Excel`}
            title="Descargar el detalle en Excel"
          >
            {glifo}
          </button>
        </div>
        {globo}
      </div>
    )
  }

  if (download) {
    return (
      <button
        type="button"
        onClick={descargar}
        className={`${baseCls}${linkCls} block w-full text-left`}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        aria-label={`${title}: descargar el detalle en Excel`}
      >
        {inner}
      </button>
    )
  }

  if (linkTo) {
    return (
      <Link
        to={linkTo}
        className={`${baseCls}${linkCls} block`}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        {inner}
      </Link>
    )
  }

  return (
    <div className={baseCls} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {inner}
    </div>
  )
}

// ─── Trend Chart ──────────────────────────────────────────────────────────────

function TrendChart({ data, currency }: {
  data: DashboardMetrics['trend_6m']
  currency: string
}) {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-content-subtle text-sm">
        Sin datos de tendencia
      </div>
    )
  }

  const chartData = data.map(d => ({
    month: d.month.slice(5, 7) + '/' + d.month.slice(2, 4),
    'Ventas': parseFloat(d.sales_total),
    'Compras': parseFloat(d.purchases_total),
    'Cobranza': parseFloat(d.collections_total),
  }))

  const fmtCur = (v: number) => fmt(v, true, currency)

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--color-divider))" />
        <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'rgb(var(--color-content-muted))' }} stroke="rgb(var(--color-divider))" />
        <YAxis
          tickFormatter={(v) => {
            if (v >= 1_000_000) return `${(v/1_000_000).toFixed(1)}M`
            if (v >= 1_000) return `${(v/1_000).toFixed(0)}K`
            return String(v)
          }}
          tick={{ fontSize: 11, fill: 'rgb(var(--color-content-muted))' }}
          stroke="rgb(var(--color-divider))"
        />
        <Tooltip
          formatter={(v) => fmtCur(v as number)}
          contentStyle={{
            backgroundColor: 'rgb(var(--color-popover))',
            border: '1px solid rgb(var(--color-divider))',
            borderRadius: 8,
            color: 'rgb(var(--color-content))',
          }}
          labelStyle={{ color: 'rgb(var(--color-content))' }}
        />
        <Legend wrapperStyle={{ fontSize: 12, color: 'rgb(var(--color-content-muted))' }} />
        <Bar dataKey="Ventas" fill="#3b82f6" radius={[4, 4, 0, 0]} />
        <Bar dataKey="Compras" fill="#f59e0b" radius={[4, 4, 0, 0]} />
        <Bar dataKey="Cobranza" fill="#10b981" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

// ─── Tab: Resumen General ─────────────────────────────────────────────────────

function ResumenTab({ metrics, isLoading, currency }: {
  metrics?: DashboardMetrics; isLoading: boolean; currency: string
}) {
  const fmtCur = (v: string | number | undefined) => fmt(v, true, currency)

  // KPIs de centros de costo.
  // ⚠️ El conteo y el resultado vienen del backend calculados sobre TODOS los centros.
  // Derivarlos de `cost_center_distribution` —que es el top 10 para la tabla— daba dos
  // números falsos: «10 centros» cuando había 17, y un resultado $160 millones mejor que
  // el real, porque los centros que solo tienen gastos son justo los que caen del corte.
  const ccData = metrics?.cost_center_distribution ?? []
  const ccCount = metrics?.cost_centers_summary?.count ?? ccData.length
  const topCCIngreso = ccData.length > 0
    ? ccData.reduce((best, cc) => parseFloat(cc.total_haber) > parseFloat(best.total_haber) ? cc : best, ccData[0])
    : null
  const ccResultadoNeto = parseFloat(metrics?.cost_centers_summary?.net_result ?? '0')

  return (
    <div className="space-y-6 relative">
      <DashboardBackdrop />
      {/* Row 1: 4 KPI cards financieros */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Ventas mes"
          value={isLoading ? '...' : fmtCur(metrics?.documents.amount_this_month)}
          subtitle={`${metrics?.documents.total_this_month ?? 0} documentos`}
          icon={FileText} color="blue"
          tooltip="Total facturado en el mes: facturas, boletas y notas de débito emitidas, menos las notas de crédito. No incluye guías de despacho ni facturas de compra."
        />
        <KPICard
          title="Compras mes"
          value={isLoading ? '...' : fmtCur(metrics?.purchases_kpis?.invoiced_this_month)}
          subtitle="Facturas de proveedor"
          icon={ShoppingBag} color="amber"
          tooltip="Suma de todas las facturas de proveedores registradas en el mes actual."
        />
        <KPICard
          title="Cobrado mes"
          value={isLoading ? '...' : fmtCur(metrics?.collections.collected_this_month)}
          subtitle="Pagos del mes"
          icon={TrendingUp} color="green"
          tooltip="Total de pagos de clientes recibidos y confirmados durante el mes actual."
        />
        <KPICard
          title="Posición neta"
          value={isLoading ? '...' : fmtCur(metrics?.balance?.net_position)}
          subtitle="CxC - CxP"
          icon={Scale} color="teal"
          tooltip="Diferencia entre las cuentas por cobrar y las cuentas por pagar a la fecha «Hasta». Un valor positivo indica que los clientes deben más de lo que se debe a proveedores."
        />
      </div>

      {/* Row 2: CxC, CxP + KPIs Centros de Costo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* ⚠️ Estas dos tarjetas son SALDOS ACUMULADOS AL «HASTA» del rango: lo que se
            debía a esa fecha, sin importar cuándo se emitió cada factura (un saldo no se
            filtra por emisión). El ícono descarga el detalle que suma exactamente ese
            monto, al mismo corte; el resto de la tarjeta lleva a la pantalla del módulo. */}
        <KPICard
          title="Por cobrar"
          value={isLoading ? '...' : fmtCur(metrics?.balance?.total_receivable)}
          subtitle={`Vencido: ${fmtCur(metrics?.balance?.overdue_receivable)} · al ${fmtFecha(metrics?.balance?.as_of_date)}`}
          icon={DollarSign} color="orange"
          tooltip={`Saldo pendiente de cobro a clientes al ${fmtFecha(metrics?.balance?.as_of_date)}, por facturas aceptadas: lo acumulado a la fecha «Hasta», sin importar cuándo se emitió cada factura. ${metrics?.balance?.receivable_count ?? 0} documentos, ${metrics?.balance?.receivable_overdue_count ?? 0} vencidos. La tarjeta lleva a Cobranza; el ícono descarga el detalle en Excel.`}
          linkTo="/receivables/collections"
          download={{
            path: rutaSaldo('/finance/receivables/outstanding/export/', metrics?.balance?.as_of_date),
            filename: `por_cobrar_${metrics?.balance?.as_of_date ?? 'hoy'}.xlsx`,
          }}
        />
        <KPICard
          title="Por pagar"
          value={isLoading ? '...' : fmtCur(metrics?.purchases_kpis?.pending_payable)}
          subtitle={`${metrics?.purchases_kpis?.overdue_payable ?? 0} vencidas · al ${fmtFecha(metrics?.balance?.as_of_date)}`}
          icon={AlertTriangle} color="purple"
          tooltip={`Saldo adeudado a proveedores al ${fmtFecha(metrics?.balance?.as_of_date)} por facturas pendientes o aprobadas: lo acumulado a la fecha «Hasta», sin importar cuándo se emitió cada factura. ${metrics?.balance?.payable_count ?? 0} facturas. La tarjeta lleva a Pagos; el ícono descarga el detalle en Excel.`}
          linkTo="/payables/payments"
          download={{
            path: rutaSaldo('/finance/payables/outstanding/export/', metrics?.balance?.as_of_date),
            filename: `por_pagar_${metrics?.balance?.as_of_date ?? 'hoy'}.xlsx`,
          }}
        />
        <KPICard
          title="Centros de costo"
          value={isLoading ? '...' : String(ccCount)}
          subtitle={topCCIngreso ? `Top: ${topCCIngreso.name}` : 'Sin movimientos'}
          icon={Building2} color="teal"
          tooltip="Cantidad de centros de costo con movimientos contables aprobados en el mes actual. El subtítulo muestra el centro con mayor ingreso."
        />
        <KPICard
          title="Resultado CC"
          value={isLoading ? '...' : fmtCur(ccResultadoNeto)}
          subtitle="Ingresos - Gastos (mes)"
          icon={TrendingUp} color="blue"
          tooltip="Resultado neto de todos los centros de costo en el mes: ingresos (haber) menos gastos (debe) de vouchers aprobados."
        />
      </div>

      {/* Trend chart */}
      <div className="bg-surface/80 backdrop-blur-sm rounded-xl border border-divider shadow-sm p-5">
        <h3 className="text-sm font-semibold text-content mb-4">Tendencia 6 meses</h3>
        <TrendChart data={metrics?.trend_6m} currency={currency} />
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top clientes */}
        <div className="bg-surface/80 backdrop-blur-sm rounded-xl border border-divider shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users size={15} className="text-primary" />
            <h3 className="text-sm font-semibold text-content">Top 5 Clientes (mes actual)</h3>
          </div>
          {isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-8 bg-surface-muted rounded animate-pulse" />)}</div>
          ) : (metrics?.top_customers ?? []).length === 0 ? (
            <p className="text-sm text-content-subtle text-center py-4">Sin ventas aceptadas este mes</p>
          ) : (
            <div className="space-y-2">
              {(metrics?.top_customers ?? []).map((c, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-divider last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-5 h-5 rounded-full bg-primary-100 text-primary text-xs flex items-center justify-center shrink-0 font-medium">{i + 1}</span>
                    <span className="text-sm text-content truncate">{c.customer_name}</span>
                    <span className="text-xs text-content-subtle shrink-0">{c.doc_count} docs</span>
                  </div>
                  <span className="text-sm font-semibold text-primary shrink-0 ml-2">{fmtCur(c.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Docs recientes */}
        <div className="bg-surface/80 backdrop-blur-sm rounded-xl border border-divider shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={15} className="text-primary" />
            <h3 className="text-sm font-semibold text-content">Documentos Recientes</h3>
          </div>
          {isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-surface-muted rounded animate-pulse" />)}</div>
          ) : (metrics?.recent_documents ?? []).length === 0 ? (
            <p className="text-sm text-content-subtle text-center py-4">Sin documentos recientes</p>
          ) : (
            <div className="space-y-1.5">
              {(metrics?.recent_documents ?? []).map(doc => (
                <div key={doc.id} className="flex items-center justify-between py-1.5 border-b border-divider last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <StatusBadge status={doc.status} catalog={DTE_STATUS} />
                    <span className="text-sm text-content truncate">{doc.customer_name}</span>
                    {doc.folio && <span className="text-xs text-content-subtle shrink-0">#{doc.folio}</span>}
                  </div>
                  <span className="text-sm font-semibold text-content shrink-0 ml-2">{fmtCur(doc.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Cost Center Distribution */}
      {(metrics?.cost_center_distribution ?? []).length > 0 && (
        <div className="bg-surface/80 backdrop-blur-sm rounded-xl border border-divider shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={15} className="text-info-solid" />
            <h3 className="text-sm font-semibold text-content">Distribución por Centro de Costo (mes actual)</h3>
          </div>
          <DataTable
            columns={[
              {
                key: 'name', header: 'Centro de Costo',
                render: cc => (
                  <>
                    <span className="text-xs text-content-subtle font-mono mr-1.5">{cc.code}</span>
                    <span className="text-content">{cc.name}</span>
                  </>
                ),
              },
              {
                key: 'total_haber', header: 'Ingresos', numeric: true,
                render: cc => <span className="font-medium text-success-content-soft">{fmtCur(parseFloat(cc.total_haber))}</span>,
              },
              {
                key: 'total_debe', header: 'Gastos', numeric: true,
                render: cc => <span className="font-medium text-danger-content-soft">{fmtCur(parseFloat(cc.total_debe))}</span>,
              },
              {
                key: 'resultado', header: 'Resultado', numeric: true,
                sortValue: cc => parseFloat(cc.total_haber) - parseFloat(cc.total_debe),
                render: cc => {
                  const r = parseFloat(cc.total_haber) - parseFloat(cc.total_debe)
                  return (
                    <span className={`font-semibold ${r >= 0 ? 'text-success-content-soft' : 'text-danger-content-soft'}`}>
                      {fmtCur(r)}
                    </span>
                  )
                },
              },
            ]}
            rows={metrics?.cost_center_distribution ?? []}
            rowKey={cc => cc.cost_center_id}
            // Ya vive dentro de la tarjeta con el título: un segundo panel se
            // vería como una caja dentro de otra.
            surface={false}
            minWidth={520}
            caption="Distribución por centro de costo del mes"
          />
        </div>
      )}
    </div>
  )
}

// ─── Tab: Ventas ──────────────────────────────────────────────────────────────

function VentasTab({ metrics, isLoading, currency }: {
  metrics?: DashboardMetrics; isLoading: boolean; currency: string
}) {
  const fmtCur = (v: string | number | undefined) => fmt(v, true, currency)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Ventas mes"
          value={isLoading ? '...' : fmtCur(metrics?.documents.amount_this_month)}
          subtitle={`${metrics?.documents.total_this_month ?? 0} documentos`}
          icon={FileText} color="blue"
          tooltip="Total facturado en el mes actual por documentos de venta emitidos."
        />
        <KPICard
          title="Clientes activos"
          value={isLoading ? '...' : String(metrics?.customers.active ?? 0)}
          subtitle={`+${metrics?.customers.new_this_month ?? 0} este mes`}
          icon={Users} color="green"
          tooltip="Cantidad de clientes con estado activo en el sistema. El subtítulo muestra cuántos se crearon este mes."
        />
        <KPICard
          title="Por cobrar del período"
          value={isLoading ? '...' : fmtCur(metrics?.collections.total_outstanding)}
          subtitle={`Vencido: ${fmtCur(metrics?.collections.overdue)}`}
          icon={DollarSign} color="amber"
          tooltip="Saldo pendiente SOLO de las facturas emitidas dentro del rango de fechas elegido, descontando los pagos recibidos. Es distinto de «Por cobrar» del Resumen General, que es el saldo total a la fecha."
          linkTo="/receivables/collections"
        />
        <KPICard
          title="Cobrado mes"
          value={isLoading ? '...' : fmtCur(metrics?.collections.collected_this_month)}
          subtitle="Pagos del mes"
          icon={TrendingUp} color="purple"
          tooltip="Suma de pagos de clientes registrados o confirmados durante el mes en curso."
        />
      </div>

      {/* Top deudores */}
      <div className="bg-surface/80 backdrop-blur-sm rounded-xl border border-divider shadow-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle size={15} className="text-warning-solid" />
          <h3 className="text-sm font-semibold text-content">Top 5 Deudores</h3>
        </div>
        {isLoading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-8 bg-surface-muted rounded animate-pulse" />)}</div>
        ) : (metrics?.top_debtors ?? []).length === 0 ? (
          <p className="text-sm text-content-subtle text-center py-4">Sin deudas pendientes</p>
        ) : (
          <div className="space-y-2">
            {(metrics?.top_debtors ?? []).map((d, i) => (
              <div key={d.customer_id} className="flex items-center justify-between py-1.5 border-b border-divider last:border-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-5 h-5 rounded-full bg-warning-surface-strong text-warning-content-soft text-xs flex items-center justify-center shrink-0 font-medium">{i + 1}</span>
                  <span className="text-sm text-content truncate">{d.customer_name}</span>
                </div>
                <span className="text-sm font-semibold text-warning-content-soft shrink-0 ml-2">{fmtCur(d.outstanding)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Tab: Compras ─────────────────────────────────────────────────────────────

function ComprasTab({ metrics, isLoading, currency }: {
  metrics?: DashboardMetrics; isLoading: boolean; currency: string
}) {
  const fmtCur = (v: string | number | undefined) => fmt(v, true, currency)
  const pkpis = metrics?.purchases_kpis

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Facturado mes"
          value={isLoading ? '...' : fmtCur(pkpis?.invoiced_this_month)}
          subtitle="Facturas de proveedor"
          icon={ShoppingBag} color="amber"
          tooltip="Total de facturas de proveedores registradas en el mes actual."
        />
        <KPICard
          title="Por pagar"
          value={isLoading ? '...' : fmtCur(pkpis?.pending_payable)}
          subtitle={`${metrics?.balance?.payable_count ?? 0} facturas · al ${fmtFecha(metrics?.balance?.as_of_date)}`}
          icon={AlertTriangle} color="orange"
          tooltip={`Saldo adeudado a proveedores al ${fmtFecha(metrics?.balance?.as_of_date)}, por facturas pendientes o aprobadas: lo acumulado a la fecha «Hasta», sin importar cuándo se emitió cada factura. La tarjeta lleva a Pagos; el ícono descarga el detalle en Excel.`}
          linkTo="/payables/payments"
          download={{
            path: rutaSaldo('/finance/payables/outstanding/export/', metrics?.balance?.as_of_date),
            filename: `por_pagar_${metrics?.balance?.as_of_date ?? 'hoy'}.xlsx`,
          }}
        />
        <KPICard
          title="Pagado mes"
          value={isLoading ? '...' : fmtCur(pkpis?.paid_this_month)}
          subtitle="Pagos del mes"
          icon={TrendingDown} color="purple"
          tooltip="Suma de pagos a proveedores confirmados durante el mes en curso."
        />
        <KPICard
          title="Facturas vencidas"
          value={isLoading ? '...' : String(pkpis?.overdue_payable ?? 0)}
          subtitle="Requieren atención"
          icon={AlertTriangle} color="teal"
          tooltip="Cantidad de facturas de proveedor aprobadas cuya fecha de vencimiento ya pasó."
        />
      </div>

      <div className="bg-glass-3 rounded-xl p-5 border border-divider">
        <p className="text-sm text-content-muted text-center">
          Ver análisis detallado en <a href="/purchases/reports" className="text-primary font-medium hover:underline">Reportes de Compras</a>
        </p>
      </div>
    </div>
  )
}

// ─── Currency Ticker ──────────────────────────────────────────────────────────

function CurrencyTicker() {
  const { data } = useCurrencyRates()
  const rates = data?.rates
  if (!rates || Object.keys(rates).length === 0) return null

  const order = ['USD', 'EUR', 'UF']
  const labels: Record<string, string> = { USD: 'USD', EUR: 'EUR', UF: 'UF' }

  return (
    <div className="flex items-center gap-3">
      {order.map(code => {
        const info = rates[code]
        if (!info) return null
        return (
          <div key={code} className="flex items-center gap-1.5 text-xs text-content-muted">
            <span className="font-semibold text-content-muted">{labels[code]}</span>
            <span>${parseFloat(info.rate).toLocaleString('es-CL')}</span>
          </div>
        )
      })}
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'resumen', label: 'Resumen General' },
  { id: 'ventas', label: 'Ventas' },
  { id: 'compras', label: 'Compras' },
] as const

type TabId = typeof TABS[number]['id']

export function DashboardPage() {
  const { user, activeTenant } = useAuthStore()

  // Date filter state
  const todayStr = todayISO()
  const firstOfMonth = todayStr.substring(0, 7) + '-01'
  const [dateFrom, setDateFrom] = useState(firstOfMonth)
  const [dateTo, setDateTo] = useState(todayStr)

  const isDefaultRange = dateFrom === firstOfMonth && dateTo === todayStr

  const { data: metrics, isLoading } = useDashboardMetrics({ date_from: dateFrom, date_to: dateTo })
  const [activeTab, setActiveTab] = useState<TabId>('resumen')

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Buenos días' : hour < 19 ? 'Buenas tardes' : 'Buenas noches'

  const currency = metrics?.currency ?? 'CLP'

  return (
    <div data-tour="dashboard" className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        title={`${greeting}, ${user?.full_name?.split(' ')[0]}`}
        subtitle={`${activeTenant?.name} · ${activeTenant?.roles.slice(0, 2).map((r) => ROLE_NAMES[r as RoleType] || r).join(', ')}${metrics ? ` · ${dateFrom} al ${dateTo}` : ''}`}
        help={HELP['dashboard']}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <label htmlFor="dashboardpage-desde" className="text-xs text-content-muted">Desde</label>
            <DatePicker id="dashboardpage-desde" value={dateFrom} onChange={setDateFrom} />
          </div>
          <div className="flex items-center gap-1.5">
            <label htmlFor="dashboardpage-hasta" className="text-xs text-content-muted">Hasta</label>
            <DatePicker id="dashboardpage-hasta" value={dateTo} onChange={setDateTo} />
          </div>
          {!isDefaultRange && (
            <button
              onClick={() => { setDateFrom(firstOfMonth); setDateTo(todayStr) }}
              className="text-xs text-primary hover:text-primary-hover font-medium whitespace-nowrap"
            >
              Restablecer mes actual
            </button>
          )}
        </div>
      </PageHeader>

      {/* Tabs + Currency rates */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex gap-1 bg-glass-3 p-1 rounded-xl w-fit border border-divider">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary text-on-primary shadow-sm'
                  : 'text-content-muted hover:text-content hover:bg-soft'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <CurrencyTicker />
      </div>

      {/* Tab content */}
      {activeTab === 'resumen' && <ResumenTab metrics={metrics} isLoading={isLoading} currency={currency} />}
      {activeTab === 'ventas' && <VentasTab metrics={metrics} isLoading={isLoading} currency={currency} />}
      {activeTab === 'compras' && <ComprasTab metrics={metrics} isLoading={isLoading} currency={currency} />}
    </div>
  )
}
